// src/app/transfer.ts
import pLimit from "p-limit";

import { encodeKey, FileItem } from "../FileGrid";
import { TransferTask } from "./transferQueue";
import { getAuthHeaders, getCurrentUser, updateOptimisticQuota } from "./auth";

const WEBDAV_ENDPOINT = "/webdav/";

export async function fetchPath(path: string) {
  const res = await fetch(`${WEBDAV_ENDPOINT}${encodeKey(path)}`, {
    method: "PROPFIND",
    headers: {
      Depth: "1",
      ...getAuthHeaders(),
    },
  });

  if (res.status === 507) {
    throw new Error("存储配额不足 (507 Insufficient Storage)");
  }
  if (!res.ok) {
    if (res.status === 401) {
      throw new Error("未授权访问，请先登录");
    }
    throw new Error(`Failed to fetch (HTTP ${res.status})`);
  }
  if (!res.headers.get("Content-Type")?.includes("application/xml"))
    throw new Error("Invalid response");

  const parser = new DOMParser();
  const text = await res.text();
  const document = parser.parseFromString(text, "application/xml");
  const items: FileItem[] = Array.from(document.querySelectorAll("response"))
    .filter(
      (response) =>
        decodeURIComponent(
          response.querySelector("href")?.textContent ?? ""
        ).slice(WEBDAV_ENDPOINT.length) !== path.replace(/\/$/, "")
    )
    .map((response) => {
      const href = response.querySelector("href")?.textContent;
      if (!href) throw new Error("Invalid response");
      const contentType = response.querySelector("getcontenttype")?.textContent;
      const size = response.querySelector("getcontentlength")?.textContent;
      const lastModified =
        response.querySelector("getlastmodified")?.textContent;
      const thumbnail = response.getElementsByTagNameNS(
        "flaredrive",
        "thumbnail"
      )[0]?.textContent;
      return {
        key: decodeURI(href).replace(/^\/webdav\//, ""),
        size: size ? Number(size) : 0,
        uploaded: lastModified!,
        httpMetadata: { contentType: contentType! },
        customMetadata: { thumbnail },
      } as FileItem;
    });
  return items;
}

const THUMBNAIL_SIZE = 144;

export async function generateThumbnail(file: File) {
  const canvas = document.createElement("canvas");
  canvas.width = THUMBNAIL_SIZE;
  canvas.height = THUMBNAIL_SIZE;
  const ctx = canvas.getContext("2d")!;

  if (file.type.startsWith("image/")) {
    const image = await new Promise<HTMLImageElement>((resolve) => {
      const img = new Image();
      img.onload = () => resolve(img);
      img.src = URL.createObjectURL(file);
    });
    ctx.drawImage(image, 0, 0, THUMBNAIL_SIZE, THUMBNAIL_SIZE);
  } else if (file.type === "video/mp4") {
    // Generate thumbnail from video
    const video = await new Promise<HTMLVideoElement>(
      async (resolve, reject) => {
        const vid = document.createElement("video");
        vid.muted = true;
        vid.src = URL.createObjectURL(file);
        setTimeout(() => reject(new Error("Video load timeout")), 2000);
        await vid.play();
        vid.pause();
        vid.currentTime = 0;
        resolve(vid);
      }
    );
    ctx.drawImage(video, 0, 0, THUMBNAIL_SIZE, THUMBNAIL_SIZE);
  } else if (file.type === "application/pdf") {
    const pdfjsLib = await import(
      // @ts-ignore
      "https://cdnjs.cloudflare.com/ajax/libs/pdf.js/4.4.168/pdf.min.mjs"
    );
    pdfjsLib.GlobalWorkerOptions.workerSrc =
      "https://cdnjs.cloudflare.com/ajax/libs/pdf.js/4.4.168/pdf.worker.min.mjs";
    const pdf = await pdfjsLib.getDocument(URL.createObjectURL(file)).promise;
    const page = await pdf.getPage(1);
    const { width, height } = page.getViewport({ scale: 1 });
    const scale = THUMBNAIL_SIZE / Math.max(width, height);
    const viewport = page.getViewport({ scale });
    const renderContext = { canvasContext: ctx, viewport };
    await page.render(renderContext).promise;
  }

  const thumbnailBlob = await new Promise<Blob>((resolve) =>
    canvas.toBlob((blob) => resolve(blob!))
  );

  return thumbnailBlob;
}

export async function blobDigest(blob: Blob) {
  const digest = await crypto.subtle.digest("SHA-1", await blob.arrayBuffer());
  const digestArray = Array.from(new Uint8Array(digest));
  const digestHex = digestArray
    .map((b) => b.toString(16).padStart(2, "0"))
    .join("");
  return digestHex;
}

export const SIZE_LIMIT = 100 * 1000 * 1000; // 100MB

function xhrFetch(
  url: RequestInfo | URL,
  requestInit: RequestInit & {
    onUploadProgress?: (progressEvent: ProgressEvent) => void;
  }
) {
  return new Promise<Response>((resolve, reject) => {
    const xhr = new XMLHttpRequest();
    xhr.upload.onprogress = requestInit.onUploadProgress ?? null;
    xhr.open(
      requestInit.method ?? "GET",
      url instanceof Request ? url.url : url
    );

    const headers = new Headers(requestInit.headers);
    // 确保携带认证凭据
    const authHeaders = getAuthHeaders();
    for (const [key, value] of Object.entries(authHeaders)) {
      if (!headers.has(key)) {
        headers.set(key, value);
      }
    }

    headers.forEach((value, key) => xhr.setRequestHeader(key, value));

    xhr.onload = () => {
      const respHeaders = xhr
        .getAllResponseHeaders()
        .trim()
        .split("\r\n")
        .reduce((acc, header) => {
          const [key, value] = header.split(": ");
          if (key) acc[key] = value;
          return acc;
        }, {} as Record<string, string>);

      // 捕获 507 存储配额不足
      if (xhr.status === 507) {
        reject(new Error("存储配额不足 (507 Insufficient Storage)"));
        return;
      }
      if (xhr.status === 403) {
        reject(new Error("权限不足，只读账号无法修改或上传文件"));
        return;
      }
      if (xhr.status >= 400) {
        reject(new Error(xhr.responseText || `请求失败 (HTTP ${xhr.status})`));
        return;
      }

      resolve(new Response(xhr.responseText, { status: xhr.status, headers: respHeaders }));
    };

    xhr.onerror = () => reject(new Error("网络请求发生异常，无法连接到服务器"));

    if (
      requestInit.body instanceof Blob ||
      typeof requestInit.body === "string"
    ) {
      xhr.send(requestInit.body);
    } else {
      xhr.send();
    }
  });
}

export async function multipartUpload(
  key: string,
  file: File,
  options?: {
    headers?: Record<string, string>;
    onUploadProgress?: (progressEvent: {
      loaded: number;
      total: number;
    }) => void;
  }
) {
  const authHeaders = getAuthHeaders();
  const headers: Record<string, string> = {
    ...authHeaders,
    ...(options?.headers || {}),
  };
  headers["content-type"] = file.type || "application/octet-stream";

  // 规范核心：分块上传 ?uploads 初始化请求头中携带 x-file-total-size
  headers["x-file-total-size"] = file.size.toString();

  const uploadResponse = await fetch(`/webdav/${encodeKey(key)}?uploads`, {
    headers,
    method: "POST",
  });

  // 处理 507 错误码
  if (uploadResponse.status === 507) {
    throw new Error("存储配额不足 (507 Insufficient Storage)");
  }
  if (!uploadResponse.ok) {
    const errorText = await uploadResponse.text();
    if (errorText.includes("507") || errorText.toLowerCase().includes("insufficient")) {
      throw new Error("存储配额不足 (507 Insufficient Storage)");
    }
    throw new Error(errorText || `初始化分块上传失败 (HTTP ${uploadResponse.status})`);
  }

  const { uploadId } = await uploadResponse.json<{ uploadId: string }>();
  const totalChunks = Math.ceil(file.size / SIZE_LIMIT);

  const limit = pLimit(2);
  const parts = Array.from({ length: totalChunks }, (_, i) => i + 1);
  const partsLoaded = Array.from({ length: totalChunks + 1 }, () => 0);
  const promises = parts.map((i) =>
    limit(async () => {
      const chunk = file.slice((i - 1) * SIZE_LIMIT, i * SIZE_LIMIT);
      const searchParams = new URLSearchParams({
        partNumber: i.toString(),
        uploadId,
      });
      const uploadUrl = `/webdav/${encodeKey(key)}?${searchParams}`;
      if (i === limit.concurrency)
        await new Promise((resolve) => setTimeout(resolve, 1000));

      const uploadPart = () =>
        xhrFetch(uploadUrl, {
          method: "PUT",
          headers,
          body: chunk,
          onUploadProgress: (progressEvent) => {
            partsLoaded[i] = progressEvent.loaded;
            options?.onUploadProgress?.({
              loaded: partsLoaded.reduce((a, b) => a + b),
              total: file.size,
            });
          },
        });

      const retryReducer = (acc: Promise<Response>) =>
        acc
          .then((res) => {
            const retryAfter = res.headers.get("retry-after");
            if (!retryAfter) return res;
            return uploadPart();
          })
          .catch((err) => {
            if (err?.message?.includes("507") || err?.message?.includes("存储配额不足")) {
              throw err;
            }
            return uploadPart();
          });
      const response = await [1, 2].reduce(retryReducer, uploadPart());
      return { partNumber: i, etag: response.headers.get("etag")! };
    })
  );
  const uploadedParts = await Promise.all(promises);
  const completeParams = new URLSearchParams({ uploadId });
  const response = await fetch(`/webdav/${encodeKey(key)}?${completeParams}`, {
    method: "POST",
    headers: {
      ...authHeaders,
      "Content-Type": "application/json",
    },
    body: JSON.stringify({ parts: uploadedParts }),
  });

  if (response.status === 507) {
    throw new Error("存储配额不足 (507 Insufficient Storage)");
  }
  if (!response.ok) {
    const errorText = await response.text();
    if (errorText.includes("507") || errorText.toLowerCase().includes("insufficient")) {
      throw new Error("存储配额不足 (507 Insufficient Storage)");
    }
    throw new Error(errorText);
  }
  return response;
}

export async function copyPaste(source: string, target: string, move = false) {
  const uploadUrl = `${WEBDAV_ENDPOINT}${encodeKey(source)}`;
  const destinationUrl = new URL(
    `${WEBDAV_ENDPOINT}${encodeKey(target)}`,
    window.location.href
  );
  const res = await fetch(uploadUrl, {
    method: move ? "MOVE" : "COPY",
    headers: {
      Destination: destinationUrl.href,
      ...getAuthHeaders(),
    },
  });

  if (res.status === 507) {
    throw new Error("存储配额不足 (507 Insufficient Storage)");
  }
  if (res.status === 403) {
    throw new Error("权限不足，只读账号无法移动或重命名文件");
  }
  if (!res.ok) {
    throw new Error(await res.text());
  }
}

export async function createFolder(cwd: string, folderName: string) {
  if (!folderName) return;
  if (folderName.includes("/")) {
    throw new Error("无效的文件夹名称，不能包含 /");
  }
  const folderKey = `${cwd}${folderName}`;
  const uploadUrl = `${WEBDAV_ENDPOINT}${encodeKey(folderKey)}`;
  const res = await fetch(uploadUrl, {
    method: "MKCOL",
    headers: getAuthHeaders(),
  });

  if (res.status === 507) {
    throw new Error("存储配额不足 (507 Insufficient Storage)");
  }
  if (res.status === 403) {
    throw new Error("权限不足，只读账号无法创建文件夹");
  }
  if (!res.ok) {
    throw new Error("创建文件夹失败");
  }
}

export async function deleteFile(key: string) {
  const uploadUrl = `${WEBDAV_ENDPOINT}${encodeKey(key)}`;
  const res = await fetch(uploadUrl, {
    method: "DELETE",
    headers: getAuthHeaders(),
  });

  if (res.status === 403) {
    throw new Error("权限不足，只读账号无法删除文件");
  }
  if (!res.ok) {
    throw new Error(`删除失败 (HTTP ${res.status})`);
  }
}

export async function processTransferTask({
  task,
  onTaskProgress,
}: {
  task: TransferTask;
  onTaskProgress?: (event: { loaded: number; total: number }) => void;
}) {
  const { remoteKey, file } = task;
  if (task.type !== "upload" || !file) throw new Error("Invalid task");

  // 【核心功能：上传前存储配额预检】
  // 如果已获取用户配额且 file.size > 剩余配额，直接在前端抛出友好提示，避免浪费上传流量
  const user = getCurrentUser();
  if (user && user.quotaBytes > 0) {
    const remainingQuota = Math.max(0, user.quotaBytes - user.usedBytes);
    if (file.size > remainingQuota) {
      throw new Error("存储配额不足，无法上传");
    }
  }

  let thumbnailDigest = null;

  if (
    file.type.startsWith("image/") ||
    file.type === "video/mp4" ||
    file.type === "application/pdf"
  ) {
    try {
      const thumbnailBlob = await generateThumbnail(file);
      const digestHex = await blobDigest(thumbnailBlob);

      const thumbnailUploadUrl = `/webdav/_$flaredrive$/thumbnails/${digestHex}.png`;
      try {
        await fetch(thumbnailUploadUrl, {
          method: "PUT",
          headers: getAuthHeaders(),
          body: thumbnailBlob,
        });
        thumbnailDigest = digestHex;
      } catch (error) {
        console.log(`Upload ${digestHex}.png failed`);
      }
    } catch (error) {
      console.log(`Generate thumbnail failed`);
    }
  }

  const headers: Record<string, string> = {
    ...getAuthHeaders(),
  };
  if (thumbnailDigest) headers["fd-thumbnail"] = thumbnailDigest;

  try {
    let result: Response;
    if (file.size >= SIZE_LIMIT) {
      result = await multipartUpload(remoteKey, file, {
        headers,
        onUploadProgress: onTaskProgress,
      });
    } else {
      const uploadUrl = `${WEBDAV_ENDPOINT}${encodeKey(remoteKey)}`;
      result = await xhrFetch(uploadUrl, {
        method: "PUT",
        headers,
        body: file,
        onUploadProgress: onTaskProgress,
      });
    }

    // 上传成功后乐观更新本地配额
    updateOptimisticQuota(file.size);
    return result;
  } catch (err: any) {
    // 统一处理 507 错误码友好提示
    if (
      err?.message?.includes("507") ||
      err?.message?.includes("Insufficient Storage") ||
      err?.status === 507
    ) {
      throw new Error("存储配额不足 (507 Insufficient Storage)");
    }
    throw err;
  }
}
